import { memo, useCallback, useContext, useEffect, useMemo, useState, type FunctionComponent } from 'react';
import size from 'lodash/size';
import findIndex from 'lodash/findIndex';
import toLower from 'lodash/toLower';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import { useLazyQuery } from '@apollo/client';
// Material UI imports
import Box from '@mui/material/Box';
// Skillmore UI Components
import useQueryObject from '@empathco/ui-components/src/hooks/useQueryObject';
import useQueryCounted from '@empathco/ui-components/src/hooks/useQueryCounted';
import FetchFailedAlert from '@empathco/ui-components/src/elements/FetchFailedAlert';
import InlineAlert from '@empathco/ui-components/src/elements/InlineAlert';
import LoadingPlaceholder from '@empathco/ui-components/src/elements/LoadingPlaceholder';
import CardSection from '@empathco/ui-components/src/elements/CardSection';
import LevelDescription from '@empathco/ui-components/src/elements/LevelDescription';
// local imports
import { HIERARCHY_INTERNAL_QUERY } from '../graphql/HierarchyInternal';
import { HIERARCHY_QUERY } from '../graphql/Hierarchy';
import { COUNTRIES_QUERY } from '../graphql/Countries';
import { STATES_QUERY } from '../graphql/States';
import {
  LookupItem, State, Manager,
  CountriesDocument, StatesDocument,
} from '../graphql/types';
import { SkillEmployees } from '../models/skillEmployees';
import { SkillLevel, SKILL_LEVEL_FIRST, SKILL_LEVEL_MIN } from '../models/skill';
import { getLeaderId } from '../models/user';
import { MAX_COUNTRIES_OPTIONS, MIN_EMPLOYEES_WITH_SKILL } from '../config/params';
import { HEIRARCHY_OPTIONS } from '../helpers/graphql';
import useModels, {
  getDefaultManager, getSkillCurrentLevel, sanitizeLookup, sanitizeLookupString
} from '../helpers/models';
import { getSettingsIntValue, getSettingsStrValue } from '../helpers/context';
import { GlobalContext } from '../context/global';
import { DataContext } from '../context';
import EmployeesWithSkillFilters from '../v3/EmployeesWithSkillFilters';
import EmployeesWithSkillChart from './EmployeesWithSkillChart';
import EmployeesWithSkillList from './EmployeesWithSkillList';
import SkillActivities from './SkillActivities';
// SCSS imports
import { chartContainer, levelDescription } from './EmployeesWithSkill.module.scss';

type EmployeesWithSkillProps = {
  readonly isEmployee?: boolean;
  readonly supervisor?: boolean;
  readonly hrbp?: boolean;
  readonly uid?: string | null;
  readonly skillId?: string | null;
  readonly isInternational?: boolean | null;
  readonly reducedUI?: boolean;
  readonly resetFilters?: boolean;
  // for Storybook only
  readonly testUid?: string;
  readonly testHasTeams?: boolean;
}

const EmployeesWithSkill: FunctionComponent<EmployeesWithSkillProps> = ({
  isEmployee = false,
  supervisor = false,
  hrbp = false,
  uid,
  skillId,
  isInternational = false,
  reducedUI = false,
  resetFilters = false,
  testUid,
  testHasTeams = false
  // eslint-disable-next-line complexity, max-statements, max-lines-per-function
}) => {
  const { getDomesticCountryId, isDomesticCountryId } = useModels();
  const { user: { data: user } } = useContext(GlobalContext);
  const isAdminOnly = Boolean(user?.is_admin_only);
  const {
    coc: { data: cocData, pending: cocPending }, requireCoC,
    skill: { data: skill, pending: pendingSkill, failed: failedSkill },
    skillEmployees: { data: skillEmployees, pending, failed, params }, requireSkillEmployees,
    settings: { data: settingsData, pending: pendingSettings, failed: failedSettings },
    settingsUpdate: { pending: pendingSettingsUpdate }, updateSettings
  } = useContext(DataContext);

  const { query: getHierarchyInternal, pending: pendingHierarchyInt, results: hierarchyInt } = useQueryObject({
    data: undefined,
    key: 'hierarchyInternal',
    flatResults: true,
    lazyQuery: useLazyQuery(HIERARCHY_INTERNAL_QUERY)
  });
  const { query: getHierarchy, pending: pendingHierarchy, results: hierarchy } = useQueryObject({
    data: undefined as unknown as Manager[],
    key: 'readHierarchy',
    flatResults: true,
    lazyQuery: useLazyQuery(HIERARCHY_QUERY, HEIRARCHY_OPTIONS)
  });

  // lazy load countires
  const { query: getCountries, pending: pendingCountries, failed: failedCountries, results: countriesData } = useQueryCounted({
    data: undefined as unknown as LookupItem,
    key: 'countries',
    lazyQuery: useLazyQuery(COUNTRIES_QUERY as typeof CountriesDocument)
  });
  // lazy load states
  const { query: getStates, pending: pendingStates, failed: failedStates, results: statesData } = useQueryCounted({
    data: undefined as unknown as State,
    key: 'states',
    lazyQuery: useLazyQuery(STATES_QUERY as typeof StatesDocument)
  });

  const { level_description, activities } = skill || {};
  const { all_employees_count } = skillEmployees || {};
  const withHierarchy = !reducedUI && (supervisor || hrbp);
  const currentLevel = (hrbp && !supervisor && SKILL_LEVEL_FIRST) || (
    supervisor ? SKILL_LEVEL_MIN : getSkillCurrentLevel((pendingSkill === false && failedSkill === false &&
      skill && toLower(skill.abbr || '') === skillId && skill) || null
    )
  );
  const countries = pendingCountries ? null : countriesData;
  const states = pendingStates ? null : statesData;
  const managers = withHierarchy
    ? (!pendingHierarchyInt && !pendingHierarchy && hierarchy) || null
    : (!cocPending && cocData) || null;

  const settingsId = isAdminOnly ? undefined : (supervisor && 'skill_supply') || 'skill_employees';
  const settingsLoaded = !settingsId || (pendingSettings === false && failedSettings === false && Boolean(settingsData));
  const settings = settingsId && settingsLoaded ? settingsData : null;
  const settingsCountry = !settingsId || resetFilters ? 0 : getSettingsIntValue(settings, `${settingsId}__country`);
  const settingsState = !settingsId || resetFilters ? 0 : getSettingsIntValue(settings, `${settingsId}__state`);
  const settingsManager = !settingsId || resetFilters ? '' : getSettingsStrValue(settings, `${settingsId}__manager`);

  const skills = useMemo(() => skill ? [skill] : undefined, [skill]);

  const defaultManager = useMemo(() => (reducedUI && '0') || (withHierarchy
    ? getDefaultManager(uid, managers, false, size(managers) >= 1 ? testUid : undefined)?.id
    : sanitizeLookupString(settingsManager, managers)
  ), [reducedUI, withHierarchy, settingsManager, testUid, uid, managers]);

  const [country, setCountry] = useState<number | undefined>(reducedUI
    ? getDomesticCountryId(countries)
    : sanitizeLookup(settingsCountry, countries)
  );
  const [state, setState] = useState<number>(sanitizeLookup(settingsState, states));
  const [manager, setManager] = useState(defaultManager);
  const [hasTeams, setHasTeams] = useState(testHasTeams);

  const isDomestic = useMemo(() => isDomesticCountryId(country, countries), [countries, country, isDomesticCountryId]);
  const local = reducedUI || (!hrbp && !isInternational);

  const [selectedLevel, setSelectedLevel] = useState<SkillLevel>(currentLevel);
  useEffect(() => {
    if (isEmployee) setSelectedLevel(
      // with present implementation of EmployeesWithSkillChart it only actually works if changing from 0 to 1+
      (prevSelectedLevel) => prevSelectedLevel < SKILL_LEVEL_FIRST && currentLevel >= SKILL_LEVEL_FIRST
        ? currentLevel
        : prevSelectedLevel
    );
  }, [currentLevel, isEmployee]);

  useEffect(() => {
    getCountries?.({ variables: { ...local ? { local: true } : {}, limit: MAX_COUNTRIES_OPTIONS } });
  }, [local, getCountries]);

  useEffect(() => {
    if (isDomestic && country) getStates?.({ variables: {
      country_id: country,
      limit: MAX_COUNTRIES_OPTIONS
    } });
  }, [country, isDomestic, getStates]);

  useEffect(() => {
    if (!reducedUI && !withHierarchy) requireCoC?.();
  }, [reducedUI, withHierarchy, requireCoC]);

  useEffect(() => {
    if (withHierarchy) getHierarchyInternal?.();
  }, [withHierarchy, getHierarchyInternal]);
  useEffect(() => {
    if (withHierarchy && hierarchyInt) getHierarchy?.();
  }, [withHierarchy, hierarchyInt, getHierarchy]);

  useEffect(() => {
    if (settingsId && resetFilters && settingsLoaded) updateSettings?.({
      [`${settingsId}__country`]: 0,
      [`${settingsId}__state`]: 0,
      [`${settingsId}__manager`]: ''
    });
  }, [resetFilters, settingsId, settingsLoaded, updateSettings]);

  useEffect(() => {
    if (withHierarchy && !isUndefined(defaultManager)) setManager(
      (prevManager) => isUndefined(prevManager) ? defaultManager : prevManager
    );
  }, [withHierarchy, defaultManager]);

  useEffect(() => {
    if (!resetFilters && settingsLoaded && !pendingSettingsUpdate) {
      if (reducedUI) setCountry(getDomesticCountryId(countries));
      else if (settingsId) {
        setCountry(sanitizeLookup(getSettingsIntValue(settings, `${settingsId}__country`), countries));
        setState(sanitizeLookup(getSettingsIntValue(settings, `${settingsId}__state`), states));
        if (!withHierarchy) setManager(sanitizeLookupString(getSettingsStrValue(settings, `${settingsId}__manager`), managers));
      }
    }
  }, [
    settingsLoaded, pendingSettingsUpdate, countries, states, managers, settings,
    reducedUI, withHierarchy, settingsId, resetFilters, getDomesticCountryId
  ]);

  useEffect(() => {
    if (reducedUI) {
      const countryId = getDomesticCountryId(countries);
      if (countryId && countryId >= 1) setCountry(countryId);
    }
  }, [countries, reducedUI, getDomesticCountryId]);

  const filtersReady = settingsLoaded && (reducedUI || Boolean(managers)) && Boolean(countries) &&
    (!isDomestic || Boolean(states));

  useEffect(() => {
    if (filtersReady && skillId && !isUndefined(manager)) requireSkillEmployees?.({
      skill_id: skillId,
      state_id: isDomestic ? state : null,
      country_id: country,
      manager_id: manager
    });
  }, [filtersReady, skillId, manager, country, isDomestic, state, requireSkillEmployees]);

  const handleCountry = useCallback((value: number) => {
    const val = sanitizeLookup(value, countries);
    setCountry(val);
    if (settingsId && settingsCountry !== val) updateSettings?.({ [`${settingsId}__country`]: val });
  }, [countries, settingsCountry, settingsId, updateSettings]);

  const handleState = useCallback((value: number) => {
    const val = sanitizeLookup(value, states);
    setState(val);
    if (settingsId && settingsState !== val) updateSettings?.({ [`${settingsId}__state`]: val });
  }, [states, settingsState, updateSettings, settingsId]);

  const handleManager = useCallback((value: string, hasChildren?: boolean) => {
    const val = withHierarchy ? value : sanitizeLookupString(value, managers);
    setManager(val);
    if (withHierarchy) setHasTeams(Boolean(hasChildren));
    else if (settingsId && settingsManager !== val) updateSettings?.({ [`${settingsId}__manager`]: val });
  }, [managers, settingsId, settingsManager, withHierarchy, updateSettings]);

  const pendingAll = pending || !params || toLower(params.skill_id) !== skillId ||
    (settingsId ? pendingSettings : false) || pendingCountries || (isDomestic && pendingStates);
  const failedAny = failed || (settingsId ? failedSettings : false) || failedCountries || failedStates;
  const noCounts = (isEmployee || supervisor) && (!all_employees_count || all_employees_count < MIN_EMPLOYEES_WITH_SKILL) &&
    (!supervisor || manager !== uid);

  const disabled = pendingAll || pendingSettingsUpdate || failedCountries || failedStates;

  return (
    <>
      {settingsLoaded && !reducedUI ? (
        <CardSection flex>
          <EmployeesWithSkillFilters
              uid={(hrbp && defaultManager) || (supervisor && uid) || undefined}
              me={withHierarchy ? user?.code : undefined}
              leader={withHierarchy ? getLeaderId(user) : undefined}
              withHierarchy={withHierarchy}
              withLevel={withHierarchy}
              withLevelSearch={supervisor}
              countries={countries}
              country={country}
              setCountry={handleCountry}
              states={states}
              state={state}
              setState={handleState}
              managers={managers}
              manager={manager}
              setManager={handleManager}
              level={selectedLevel}
              setLevel={setSelectedLevel}
              disabled={disabled}
              disabledState={!isDomestic}
          />
        </CardSection>
      ) : undefined}
      {(failedAny && <FetchFailedAlert flat/>) ||
      ((!skillId || pendingAll || !skillEmployees) && <LoadingPlaceholder flat/>) || (
        <>
          <CardSection dark narrow={!noCounts} top={!noCounts} className={chartContainer}>
            {noCounts ? (
              <InlineAlert severity="info" message="skill.no_employees_with_this_skill"/>
            ) : (
              <EmployeesWithSkillChart
                  level={selectedLevel}
                  setLevel={setSelectedLevel}
                  employeeCounts={skillEmployees as SkillEmployees}
              />
            )}
          </CardSection>
          {(hrbp || (isEmployee && supervisor)) && selectedLevel >= SKILL_LEVEL_FIRST && (
            (isArray(level_description) && isString(level_description[selectedLevel])) ||
            (size(activities) >= 1 && findIndex(activities, ({ code, activity_type, name, skill_proficiency_level }) =>
              Boolean(code && activity_type && name) &&
              (!skill_proficiency_level || skill_proficiency_level === selectedLevel)
            ) >= 0)
          ) ? (
            <Box
                pt={4}
                px={4}
                pb={1}
                display="flex"
                justifyContent="center"
            >
              <Box
                  display="flex"
                  flexDirection="column"
                  className={levelDescription}
              >
                {level_description && isString(level_description?.[selectedLevel]) ? (
                  <div>
                    <LevelDescription description={level_description[selectedLevel]} level={selectedLevel}/>
                  </div>
                ) : undefined}
                {skills ? (
                  <div className={levelDescription}>
                    <SkillActivities accordion skills={skills} selectedLevel={selectedLevel} disabled={disabled}/>
                  </div>
                ) : undefined}
              </Box>
            </Box>
          ) : undefined}
          {skillId && supervisor && (manager === '0' || (hasTeams && manager !== uid) || (
            manager === uid && !pendingCountries && (!isDomestic || !pendingStates) &&
            countries && (!isDomestic || states)
          )) ? (
            <EmployeesWithSkillList
                settingsId={settingsId}
                skillId={skillId}
                asTeams={manager !== uid}
                managerId={manager}
                level={selectedLevel}
                stateId={isDomestic ? state : undefined}
                countryId={country}
            />
          ) : undefined}
        </>
      )}
    </>
  );
};

export default memo(EmployeesWithSkill);
